import { useEffect, useRef, useState } from 'react';

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  rating: number;
  initial: string;
  service: string;
}

const testimonials: Testimonial[] = [
  {
    quote: 'Got my SUV tinted here and the result is absolutely clean — perfect edges, zero bubbles, and the heat rejection is incredible. You can feel the difference immediately when you get in the car on a hot day. Highly recommend.',
    author: 'Marcus D.',
    role: 'Philadelphia, PA',
    rating: 5,
    initial: 'M',
    service: 'Automotive Tinting',
  },
  {
    quote: 'Had TINTSPRO X tint the windows in my home and they did a phenomenal job. The UV protection is real — my furniture is no longer being bleached by the sun and the interior stays noticeably cooler. Very professional team.',
    author: 'Rachel T.',
    role: 'Northeast Philadelphia',
    rating: 5,
    initial: 'R',
    service: 'Residential Tinting',
  },
  {
    quote: 'Carbon fiber tint on my truck came out perfect. The film quality is clearly premium — deep color, no glare, and they finished it same-day. Best tinting experience I\'ve had in Philly. Will bring my other vehicles here.',
    author: 'James P.',
    role: 'South Philadelphia',
    rating: 5,
    initial: 'J',
    service: 'Carbon Fiber Installation',
  },
];

export default function Testimonials() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="relative py-28 md:py-36 px-6 md:px-12 lg:px-16 overflow-hidden"
      ref={ref}
      style={{ background: 'linear-gradient(180deg, #080808 0%, #0d0d0d 100%)' }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-[#AAFF00]/3 blur-[150px]" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div
          className={`flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-[1px] bg-[#AAFF00]" />
              <span className="text-[#AAFF00] text-[11px] font-sans font-medium tracking-[0.3em] uppercase">Client Results</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl xl:text-6xl font-bold text-white leading-[1.05]">
              What Clients Say
            </h2>
          </div>
          <div className="flex items-center gap-4 bg-[#111111] border border-white/8 rounded-2xl px-6 py-4">
            <div>
              <p className="font-serif text-white text-3xl font-bold leading-none">5.0</p>
              <div className="flex items-center gap-0.5 mt-1">
                {[1,2,3,4,5].map((s) => (
                  <div key={s} className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-[#AAFF00] text-xs"></i>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-[1px] h-10 bg-white/10" />
            <div>
              <p className="text-white font-sans text-sm font-semibold">Google Rated</p>
              <p className="text-white/40 text-xs font-sans mt-0.5">Philadelphia, PA</p>
            </div>
          </div>
        </div>

        {/* 3-column grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {testimonials.map((t, i) => (
            <div
              key={i}
              className={`relative bg-[#111111] border border-white/8 rounded-2xl p-8 flex flex-col transition-all duration-700 hover:border-[#AAFF00]/20 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              <div className="absolute top-6 right-7 font-serif text-[64px] leading-none text-[#AAFF00]/8 select-none pointer-events-none">
                &ldquo;
              </div>

              {/* Service badge */}
              <div className="mb-5">
                <span className="bg-[#AAFF00]/10 border border-[#AAFF00]/20 text-[#AAFF00] text-[11px] font-sans font-medium px-3 py-1 rounded-full">
                  {t.service}
                </span>
              </div>

              <div className="flex gap-1 mb-5">
                {Array.from({ length: t.rating }).map((_, j) => (
                  <div key={j} className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-[#AAFF00] text-sm"></i>
                  </div>
                ))}
              </div>

              <p className="font-sans text-white/65 text-sm leading-relaxed flex-1 mb-7">
                &ldquo;{t.quote}&rdquo;
              </p>

              <div className="flex items-center gap-3 border-t border-white/6 pt-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#AAFF00]/15 border border-[#AAFF00]/20 shrink-0">
                  <span className="font-serif text-[#AAFF00] text-sm font-semibold">{t.initial}</span>
                </div>
                <div>
                  <p className="text-white font-sans font-semibold text-sm">{t.author}</p>
                  <p className="text-white/35 font-sans text-xs mt-0.5">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className={`mt-12 text-center transition-all duration-700 delay-500 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <p className="text-white/40 text-sm font-sans mb-4">
            Join hundreds of Philadelphia clients who&apos;ve upgraded their vehicles and properties with TINTSPRO X.
          </p>
          <a
            href="#estimate"
            className="whitespace-nowrap inline-flex items-center gap-2 border border-[#AAFF00]/40 text-[#AAFF00] text-sm font-sans font-medium px-8 py-3.5 rounded-full hover:bg-[#AAFF00] hover:text-[#080808] transition-all duration-300 cursor-pointer"
          >
            Book Your Appointment Today
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm"></i>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
